import { clearSession, currentToken, type Session } from '@/features/auth/session';
import { t } from '../strings';
import type {
  AnswerInput,
  Assessment,
  AssessmentSummary,
  ContactInput,
  MailStatus,
  Narrative,
  OrganisationType,
  PagedResult,
  Problem,
  QuestionnaireView,
  ReportView,
  ScoringResult,
} from './types';

/**
 * The HTTP layer. It carries no calculation of any kind — the backend is the only authoritative
 * calculator (CLAUDE.md, rule 1), and everything below simply moves JSON.
 */
const BASE = import.meta.env.VITE_API_BASE ?? '';

/**
 * Supplies the bearer token for every protected call. It reads the PIN session rather than
 * holding a copy, so signing out takes effect on the very next request.
 *
 * Still pluggable: tests replace it, and it is the seam an OIDC library would slot into if
 * requirement F-40 is revisited.
 */
let tokenProvider: () => Promise<string | undefined> = async () => currentToken();

export function setTokenProvider(provider: () => Promise<string | undefined>): void {
  tokenProvider = provider;
}

/** An error that already knows which Danish message the user should see. */
export class ApiError extends Error {
  readonly status: number;
  readonly userMessage: string;
  readonly problem: Problem | undefined;

  constructor(status: number, userMessage: string, problem?: Problem) {
    super(`${status}: ${userMessage}`);
    this.name = 'ApiError';
    this.status = status;
    this.userMessage = userMessage;
    this.problem = problem;
  }
}

function messageForStatus(status: number, problem?: Problem): string {
  switch (status) {
    case 400:
      return problem?.detail ?? t('fejl.ugyldigVaerdi');
    case 401:
    case 403:
      return t('fejl.ingenAdgang');
    case 409:
      return problem?.detail ?? t('fejl.afsendtKanIkkeAendres');
    case 410:
      return t('rapport.udloebet');
    case 429:
      return t('login.forMangeForsoeg');
    case 404:
      return t('rapport.ikkeFundet');
    default:
      return t('fejl.generel');
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await tokenProvider();

  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, { ...init, headers });
  } catch {
    throw new ApiError(0, t('fejl.netvaerk'));
  }

  // No body by contract: 204 from contact, admin delete and resend; 202 from the erasure
  // request, which answers the same way whether or not the token exists.
  if (response.status === 204 || response.status === 202) {
    return undefined as T;
  }

  // A token that has expired or been invalidated server-side. Drop it, so the route guard sends
  // the user to the PIN screen instead of retrying forever with a credential that cannot work.
  // The sign-in call itself is exempt: a wrong PIN there is not an expired session.
  if (response.status === 401 && path !== '/api/auth/pin') {
    clearSession();
  }

  if (!response.ok) {
    let problem: Problem | undefined;
    if (response.headers.get('content-type')?.includes('json')) {
      problem = (await response.json().catch(() => undefined)) as Problem | undefined;
    }
    throw new ApiError(response.status, messageForStatus(response.status, problem), problem);
  }

  // A successful response that is not JSON never reached the API. The usual cause is a build
  // with an empty VITE_API_BASE: /api/* then resolves against the static host, which rewrites
  // any unmatched GET to the SPA shell and answers 200 with HTML. Parsing that throws a
  // SyntaxError outside the try above, which blanks the screen instead of showing a message.
  if (!response.headers.get('content-type')?.includes('json')) {
    throw new ApiError(response.status, t('fejl.generel'));
  }

  return (await response.json()) as T;
}

export const api = {
  /**
   * Exchanges a PIN for a session token. The PIN is sent once and never stored — what comes
   * back, and what is kept, is a token that expires on its own.
   */
  signIn(pin: string): Promise<Session> {
    return request<Session>('/api/auth/pin', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
  },

  questionnaire(
    organisationType?: OrganisationType,
    scope?: 'light' | 'detaljeret',
  ): Promise<QuestionnaireView> {
    const params = new URLSearchParams();
    if (organisationType) params.set('organisationType', organisationType);
    // Without this a light assessment would be handed the full set and shown questions it is
    // not scored on. The server varies its ETag by scope for the same reason.
    if (scope) params.set('scope', scope);

    const query = params.size > 0 ? `?${params}` : '';
    return request<QuestionnaireView>(`/api/questionnaire/current${query}`);
  },

  createAssessment(body: {
    systemName: string;
    organisationType: OrganisationType;
    organisationName?: string | null;
    eventId?: string | null;
    /** "light" (8 questions) or "detaljeret" (16). Fixed for the life of the assessment. */
    scope?: 'light' | 'detaljeret';
  }): Promise<Assessment> {
    return request<Assessment>('/api/assessments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** Returns the whole result, so the header can update without a second round trip. */
  saveAnswers(id: string, answers: AnswerInput[]): Promise<ScoringResult> {
    return request<ScoringResult>(`/api/assessments/${id}/answers`, {
      method: 'PATCH',
      body: JSON.stringify({ answers }),
    });
  },

  preview(id: string): Promise<ReportView> {
    return request<ReportView>(`/api/assessments/${id}/preview`);
  },

  /**
   * Fetched separately from the preview because it can take seconds: the numbers appear at once
   * and only this part waits. Resolves to undefined on 204, which means there is nothing to
   * show rather than something went wrong.
   */
  narrative(id: string): Promise<Narrative | undefined> {
    return request<Narrative | undefined>(`/api/assessments/${id}/narrative`);
  },

  saveContact(id: string, body: ContactInput): Promise<void> {
    return request<void>(`/api/assessments/${id}/contact`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  send(id: string): Promise<{
    reportId: string;
    url: string;
    expiresAt: string;
    mailStatus: MailStatus;
  }> {
    return request(`/api/assessments/${id}/send`, { method: 'POST' });
  },

  /**
   * The self-service twins of createAssessment/saveAnswers/preview/narrative/saveContact/send.
   * No authentication on any of them — same trust model as the report link below, an assessment
   * id is an unguessable v7 GUID — and the server, not this client, decides that these count as
   * channel "selfService".
   */
  selfServiceCreateAssessment(body: {
    systemName: string;
    organisationType: OrganisationType;
    organisationName?: string | null;
    scope?: 'light' | 'detaljeret';
  }): Promise<Assessment> {
    return request<Assessment>('/api/self-service/assessments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  selfServiceSaveAnswers(id: string, answers: AnswerInput[]): Promise<ScoringResult> {
    return request<ScoringResult>(`/api/self-service/assessments/${id}/answers`, {
      method: 'PATCH',
      body: JSON.stringify({ answers }),
    });
  },

  selfServicePreview(id: string): Promise<ReportView> {
    return request<ReportView>(`/api/self-service/assessments/${id}/preview`);
  },

  selfServiceNarrative(id: string): Promise<Narrative | undefined> {
    return request<Narrative | undefined>(`/api/self-service/assessments/${id}/narrative`);
  },

  selfServiceSaveContact(id: string, body: ContactInput): Promise<void> {
    return request<void>(`/api/self-service/assessments/${id}/contact`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  selfServiceSend(id: string): Promise<{
    reportId: string;
    url: string;
    expiresAt: string;
    mailStatus: MailStatus;
  }> {
    return request(`/api/self-service/assessments/${id}/send`, { method: 'POST' });
  },

  /** No authentication: whoever holds the link has access. */
  report(token: string): Promise<ReportView> {
    return request<ReportView>(`/api/reports/${encodeURIComponent(token)}`);
  },

  requestErasure(token: string): Promise<void> {
    return request<void>(`/api/reports/${encodeURIComponent(token)}/erasure-request`, {
      method: 'POST',
    });
  },

  adminAssessments(filters: {
    eventId?: string;
    status?: string;
    recommendation?: string;
    minDependencyIndex?: number;
    page?: number;
    pageSize?: number;
  }): Promise<PagedResult<AssessmentSummary>> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') {
        query.set(key, String(value));
      }
    }
    return request<PagedResult<AssessmentSummary>>(`/api/admin/assessments?${query}`);
  },

  /** The CSV is a download, so it deliberately bypasses the JSON request helper. */
  async exportCsv(filters: Record<string, string | number | undefined>): Promise<Blob> {
    const query = new URLSearchParams({ format: 'csv' });
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') {
        query.set(key, String(value));
      }
    }

    const token = await tokenProvider();
    const response = await fetch(`${BASE}/api/admin/assessments?${query}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });

    if (!response.ok) {
      throw new ApiError(response.status, messageForStatus(response.status));
    }

    return response.blob();
  },

  resend(reportId: string, body: { recipient?: string; extendByDays?: number }): Promise<void> {
    return request<void>(`/api/admin/reports/${reportId}/resend`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  deleteAssessment(id: string): Promise<void> {
    return request<void>(`/api/admin/assessments/${id}`, { method: 'DELETE' });
  },
};
